import { Link } from "react-router-dom";
import { Lock, ShieldCheck, FileCheck, ShieldAlert, Fingerprint } from "lucide-react";
import { PageHero, SectionHead, Reveal, Card, TextLink, btnPrimary, ArrowIcon, usePageMeta } from "@/components/primitives";
import { SecurityPillars } from "@/components/blocks2";

const TRUST_PILLARS = [
  { icon: Lock, title: "Privacy by Design" },
  { icon: ShieldCheck, title: "Secure by Design" },
  { icon: FileCheck, title: "Compliance by Design" },
  { icon: ShieldAlert, title: "Fraud Prevention" },
  { icon: Fingerprint, title: "Identity Protection" },
];

const COMMITMENTS = [
  { title: "Compliance-First", body: "Regulatory alignment is a design input, not an afterthought. KYC/AML, audit trails and reporting are native to the platform's architecture." },
  { title: "Privacy by Design", body: "We collect the minimum data needed, with consent, for a stated purpose — and we say so plainly." },
  { title: "No Empty Badges", body: "This site claims no certifications, licences or regulatory approvals we do not hold. When we earn them, they will be listed here with evidence." },
  { title: "Responsible Technology", body: "AI systems are built for explainability and oversight, with humans accountable for consequential decisions." },
];

export default function TrustCenter() {
  usePageMeta(
    "TimePe Trust Center | Security, Privacy & Compliance",
    "How TimePe approaches security, privacy, fraud prevention and regulatory alignment — trust as architecture, not marketing."
  );
  return (
    <>
      <PageHero
        eyebrow="Trust Center"
        lines={["Trust Isn't a Feature.", "It's the Architecture."]}
        lead="Compliance-first, AI-driven, privacy by design, secure by design, Bharat-first — the principles TimePe is engineered around."
      />

      <section className="border-b border-line bg-abyss/40 py-16 sm:py-20" data-testid="trust-pillars-strip">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-8">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {TRUST_PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <div data-testid={`trust-pillar-${i}`} className="flex h-full items-center gap-4 rounded-2xl border border-line bg-panel p-5 shadow-[0_1px_3px_rgba(11,21,38,0.05)]">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-line bg-abyss text-electric">
                    <p.icon className="h-5 w-5" />
                  </span>
                  <p className="font-display text-sm font-semibold text-mist">{p.title}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-line py-24 sm:py-32" data-testid="trust-commitments">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-8">
          <SectionHead eyebrow="Our Commitments" title="Evidence Before Claims" />
          <div className="mt-14 grid gap-5 sm:grid-cols-2">
            {COMMITMENTS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.07}>
                <Card testid={`commitment-card-${i}`} className="h-full p-8">
                  <p className="font-mono text-xs text-teal">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-4 font-display text-xl font-semibold text-mist">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-mute">{c.body}</p>
                </Card>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15} className="mt-10">
            <p className="max-w-2xl text-sm leading-relaxed text-mute">
              "TimePe is being architected around security, privacy, regulatory alignment and responsible
              technology from the foundation."
            </p>
          </Reveal>
        </div>
      </section>

      <SecurityPillars />

      <section className="border-b border-line bg-abyss/50 py-24 sm:py-32" data-testid="trust-disclosure">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <SectionHead
              eyebrow="Responsible Disclosure"
              title="Found a Security Concern?"
              lead="If you believe you've identified a vulnerability in our systems, tell us. We take every report seriously and respond through our official channel."
            />
            <Reveal delay={0.1} className="lg:justify-self-end">
              <Link to="/contact" data-testid="disclosure-cta" className={btnPrimary}>
                Report via Contact <ArrowIcon />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="mt-12">
            <TextLink to="/contact" testid="trust-contact-link">Talk to our team about security, privacy or compliance</TextLink>
          </Reveal>
        </div>
      </section>
    </>
  );
}
